<template>
  <div class="coupon-info-dialog">
    <FitHalfScreenDialogV2
      :title="titleText"
      ref="refCouponDetailDialog"
      @onClose="onClose"
      :isAppendToBody="true">
      <div
        slot="bd"
        :class="['coupon-rule', { en: isLangEn }]"
        v-stat.brow="{
          tag: 'coupon-detail-dialog.comp-brow|组件曝光',
          ext: biExt,
        }">
        <div class="coupon-dialog-info" v-if="goodItemInfo">
          <div class="sp-info-wrapper" v-if="goodItemInfo.brandName">
            <img :src="goodItemInfo.couponIcon" alt="">
            <span class="font-32-medium">{{ goodItemInfo.brandName }}</span>
          </div>
          <div class="coupon-info-wrapper">
            <CouponV2 :lang="lang" :couponInfo="dialogShowCouponInfo"></CouponV2>
          </div>
          <div class="rule-info-wrapper">
            <div class="rule-row">
              <div class="rule-left" v-html="tI18n('有效期限')"></div>
              <div class="rule-right">
                {{ validPeriod }}
                <span class="expire-tip" v-if="isShowExpireTip">{{ tI18n('即將到期') }}</span>
              </div>
            </div>
            <div class="rule-row">
              <div class="rule-left" v-html="tI18n('適用商戶')"></div>
              <div class="rule-right">{{ goodItemInfo.couponRange }}</div>
            </div>
            <div class="rule-row">
              <div class="rule-left" v-html="tI18n('適用條件')"></div>
              <div class="rule-right" role="text" :aria-label="couponCondAriaLabel">
                {{ couponCond }}
              </div>
            </div>
          </div>
          <div class="coupon-tandc"
            v-if="isValidTnc"
            @click="onClickTnc"
            v-stat.click="{ tag: 'coupon-detail-dialog.rule-click|条款与细则点击', ext: biExt }">
            <span>{{ tI18n('條款及細則') }}</span>
            <SvgIcon class="arrow-right" icon-name="arrow-right-grey" />
          </div>
          <div class="use-scene" v-if="isSpCoupon">
            <div class="left">{{ tI18n('使用方式') }}</div>
            <div class="right">
              <span :class="['right-item second', { first: !isOnlineMerchantCp }]">
                <SvgIcon icon-name="guide-1"></SvgIcon>
                <span v-html="isOnlineMerchantCp ? tI18n('前往網上商店/App後啟用') : tI18n('到店後啟用')"></span>
              </span>
              <SvgIcon :class="['arrow-right', { online: isLangEn }]" icon-name="arrow-right-grey" />
              <span :class="['right-item', { second: isOnlineMerchantCp }]">
                <SvgIcon :icon-name="isOnlineMerchantCp ? 'guide-2-online' : 'guide-2'"></SvgIcon>
                <span v-html="isOnlineMerchantCp ? tI18n('輸入優惠碼') : tI18n('向店員出示')"></span>
              </span>
              <SvgIcon :class="['arrow-right', { online: isLangEn }]" icon-name="arrow-right-grey" />
              <span class="right-item">
                <SvgIcon icon-name="guide-3"></SvgIcon>
                <span>{{ tI18n('付款') }}</span>
              </span>
            </div>
          </div>
        </div>

        <div class="dialog-footer" v-if="btnInfo">
          <slot name="upperBtn"></slot>
          <Button
            @click="onClickBtn"
            :size="btnInfo.size"
            :disabled="btnInfo.isDisabled"
            v-if="btnInfo.isShowBtn"
            :type="btnInfo.type"
            v-stat.click="{
              tag: 'coupon-detail-dialog.btn-click|按钮点击',
              ext: biExt,
            }">{{ btnInfo.btnText }}</Button>
          <slot name="underBtn"></slot>
        </div>
      </div>
    </FitHalfScreenDialogV2>
    <!-- tnc 弹窗 -->
    <TncDialog v-if="isValidTnc && goodItemInfo"
      ref="refTncDialog"
      :tncUrl="goodItemInfo.couponTandc"
      :biExt="biExt"></TncDialog>
  </div>
</template>

<script lang="ts">
import { parseCurrencyAria } from '../coupon/utils';
import CouponV2 from '../coupon/coupon-v2.vue';
import TncDialog from '../tnc/tnc-dialog.vue';
import { CouponType, ThemeColor } from '../coupon/types'; // 这个CouponType是coupon-v2自己定义的

import Button from './common/button.vue';
import { i18n, initLang } from './i18n';
import {
  getSpCouponInfo,
  getValidPeriod,
  isOnlineMerchantCoupon,
  checkIsSpCoupon,
  checkIsDiscountCoupon,
} from './business/format-coupon';
import SvgIcon from './common/svg-icon.vue';

import { biClick } from '@tencent/fit-bi-sdk';
import { FitHalfScreenDialogV2 } from '@tencent/wphk-ui';
import { computed, defineComponent, PropType, ref } from 'vue-demi';
import { language, url } from '@tencent/ppd-base-libs';
import { ExpireType, GoodItem, UtilsTypes } from '@tencent/wphk-act-base-types';
import dayjs from 'dayjs';
import { DetailBtnConfig, ExtraDetailInfo } from './types';

export default defineComponent({
  name: 'CouponDetailDialog',
  components: {
    FitHalfScreenDialogV2,
    Button,
    CouponV2,
    SvgIcon,
    TncDialog,
  },
  props: {
    /** 弹层标题 */
    titleText: String,
    /** 券信息 */
    goodItemInfo: Object as PropType<GoodItem>,
    /** 除GoodItem以外的券信息，当前只有coupon-v2.vue使用 */
    extraInfo: Object as PropType<ExtraDetailInfo>,
    /** btn信息 */
    btnInfo: Object as PropType<DetailBtnConfig>,
    /** 当前系统时间，不传则默认new Date() */
    sysTime: String,
  },
  setup(prop, context) {
    initLang();
    // ---- ref句柄 ----
    // 半弹层
    const refCouponDetailDialog = ref<FitHalfScreenDialogV2 | null>(null);
    // tnc半弹层
    const refTncDialog = ref<InstanceType<typeof TncDialog> | null>(null);


    // ---- data/computed ----
    // 语言
    const lang = ref(language.isEn() ? 'en' : 'zh-hk');
    const isLangEn = language.isEn();
    // 是否为商户展示券
    const isSpCoupon = computed(() => checkIsSpCoupon(prop.goodItemInfo?.couponType));
    // 是否展示 tnc
    const isValidTnc = computed(() => isSpCoupon.value && (prop.goodItemInfo?.couponTandc ?? '').includes('tncId'));
    // 是否展示过期提示
    const isShowExpireTip = computed(() => {
      // 是否为固定时间类型
      const isFixedTime = prop.goodItemInfo?.expireType === ExpireType.FIXED;
      // 当前时间
      const curTime = dayjs(prop.sysTime || new Date());
      const exTime = dayjs(prop.goodItemInfo?.expireTime);
      const isNear3Day = exTime.diff(curTime, 'day') <= 3 ;
      return isFixedTime && isNear3Day;
    });
    const biExt = computed(() => (
      {
        fcouponName: prop.goodItemInfo?.couponName,
        fmtn: url.getUrlParam('mtn'), // 物料MTN
        fbatchid: url.getUrlParam('batchid'), // 物料批次Id
        fcampaignid: url.getUrlParam('campaignid'), // 物料计划Id
        fspid: prop.goodItemInfo?.spid || url.getUrlParam('spId'),  // 商户Id
        fcouponId: prop.goodItemInfo?.goodsId,  // 券Id
        // @ts-ignore
        fcouponState: prop.goodItemInfo?.couponState, // 有就上报，没有就不上报
      }
    ));
    // 有效期限
    const validPeriod = computed(() => getValidPeriod(prop.goodItemInfo));
    // 是否是线上使用展示券
    const isOnlineMerchantCp = computed(() => isOnlineMerchantCoupon(prop.goodItemInfo));
    // 使用条件
    const couponCond = computed(() => {
      if (!prop.goodItemInfo) return '';
      const { couponCond, requiredNum } = prop.goodItemInfo;
      return  isSpCoupon.value ? couponCond : i18n('消費滿HK$x可用', { requiredNum });
    });
    // 无障碍
    const couponCondAriaLabel = computed(() => parseAriaLabel(couponCond.value));
    // 展示的券信息
    const dialogShowCouponInfo = computed(() => {
      const spCouponInfo = isSpCoupon.value ? getSpCouponInfo() : {};
      const baseCouponInfo = checkIsDiscountCoupon(prop.goodItemInfo?.couponType) ? {
        couponType: CouponType.discount,
        prizeDiscount: prop.goodItemInfo?.discount,
      } : {
        couponType: CouponType.cash,
        prizeAmountYuan: prop.goodItemInfo?.discount,
      };
      return {
        // 券基本信息
        /* 优惠券奖品logo */
        prizeLogo: prop.goodItemInfo?.couponIcon,
        /* 优惠券名称 */
        prizeName: prop.goodItemInfo?.couponName,
        /* 商户图标 */
        spLogo: prop.goodItemInfo?.brandLogo,
        /* 优惠券优惠面额（元） */
        prizeAmountYuan: prop.goodItemInfo?.discount,
        /* 券包中的券数量-金额下方数量，有值则认为是券包，V2新增 */
        pkgCount: (prop.goodItemInfo?.perCount || 0) > 1 ? prop.goodItemInfo?.perCount : 0, // 券包中券数量 / 一次派发张数
        /* 货物图片，竖版券需要配置，Vertical新增 */
        goodsUrl: prop.goodItemInfo?.goodsUrl,
        /** 币种类型, 默认为CurrencyType.HKD */
        currencyType: prop.goodItemInfo?.currencyType,

        /* 优惠券主题色 */
        themeColor: ThemeColor.green,
        /* 是否展示按钮 */
        isShowButton: false,
        /* 是否展示邮戳 */
        isShowStamp: !!prop.extraInfo?.stampText,

        /* 优惠券标签文本 */
        couponTagText: prop.extraInfo?.couponTagText,
        /* 优惠券标签颜色 */
        couponTagColor: prop.extraInfo?.couponTagColor,
        /* 按钮文本 */
        couponBtnText: prop.extraInfo?.couponBtnText,
        /* 按钮类型 */
        buttonType: prop.extraInfo?.buttonType,
        /* 邮戳文本 */
        stampText: prop.extraInfo?.stampText,
        /* 优惠券折扣（折） */
        prizeDiscount: prop.extraInfo?.prizeDiscount,
        /** 是否连锁券 */
        isChain: prop.extraInfo?.isChain,
        /* 优惠券是否已过期（过期时会将券背景设置为灰色，同时按钮置灰，以及加上邮戳，因此需要设置邮戳文本） */
        isExpire: prop.extraInfo?.isExpire,

        /* 是否展示层叠阴影 */
        isShowShadow: prop.extraInfo?.isShowShadow,
        /* 优惠券提示条文本 - 右侧 */
        tipsTextRight: prop.extraInfo?.tipsTextRight,
        /* 优惠券挖空半圆颜色 */
        semicircleColor: prop.extraInfo?.semicircleColor,
        /* 券包-金额下方文案 */
        pkgInfo: prop.extraInfo?.pkgInfo,
        /* 主題券 logo */
        themeLogo: prop.extraInfo?.themeLogo,

        ...spCouponInfo,
        ...baseCouponInfo,

        /* 商户描述 */
        spDesc: '', // coupon-v2券内不展示spDesc  prop.goodItemInfo?.brandName, // TODO: couponSpname?是什么？
        /* 优惠券描述 */
        prizeDesc: '', // 不需要展示couponDesc  prop.goodItemInfo?.couponDesc,
      };
    });

    // ---- 事件 ----
    // 拉起tnc弹层
    function onClickTnc() {
      refTncDialog.value?.showTncDialog();
    }
    // 弹层关闭
    function onClose() {
      // @ts-ignore
      biClick('coupon-detail-dialog.comp-close|组件关闭', biExt.value);
      context.emit('onClose');
    };
    // 按钮点击
    function onClickBtn() {
      context.emit('onBtnClick');
    }


    // ---- 辅助方法 ----
    /** 转换为无障碍提示文案（主要是对金额处理） */
    function parseAriaLabel(text: string) {
      const { currencyType = UtilsTypes.CurrencyType.HKD } = prop.goodItemInfo ?? {};
      return parseCurrencyAria(text, true, currencyType);
    }

    return {
      refCouponDetailDialog,
      refTncDialog,

      isSpCoupon,
      isOnlineMerchantCp,
      validPeriod,
      isShowExpireTip,
      couponCond,
      couponCondAriaLabel,
      biExt,
      isLangEn,
      lang,
      dialogShowCouponInfo,
      isValidTnc,

      /** 点击“”条款与细则 */
      onClickTnc,
      /** 按钮点击 */
      onClickBtn,
      /** 半弹层关闭 */
      onClose,


      /** 供调用方使用： 打开券详情弹窗 */
      showDialog() {
        refCouponDetailDialog.value!.showDialog();
      },
      /** 供调用方使用： 关闭券详情弹窗 */
      hideDialog() {
        refCouponDetailDialog.value!.hideDialog();
      },

      tI18n: i18n,
    };
  },
});
</script>

<style lang="less" scoped>
@rem: 200rem;
@import (reference) '~@wphk-ui/styles/fonts.less';

.coupon-rule {
  color: black;
  .coupon-title-container {
    display: inline-block;
    .coupon-title {
      .font-34-medium;
    }
    .coupon-count {
      display: inline-block;
      .font-34-medium;
      .black-close {
        width: 32 / @rem;
        height: 32 / @rem;
        vertical-align: middle;
      }
      .green {
        color: #00c25f;
      }
    }
  }

  .sp-tags-list {
    display: flex;
    align-items: flex-start;
    gap: 0 4/@rem;
  }

  .sp-tag {
    margin-top: 7 / @rem;
    height: 40 / @rem;
    color: #07c160;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 4 / @rem 8 / @rem;
    border: 1px solid #07c160;
    border-radius: 8 / @rem;
    width: fit-content;
    box-sizing: border-box;

    &--yellow {
      color: #FA9D3B;
      border-color: #FA9D3B;
    }

    .icon-manual {
      display: inline-block;
      width: 24 / @rem;
      height: 24 / @rem;
      margin-right: 8 / @rem;
      flex-shrink: 0;
    }
    .sp-tag--text {
      font-size: 22 / @rem;
    }
  }
  .rule-detail {
    margin-top: 24 / @rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    .rule-title {
      .font-28-medium;
      font-weight: 700 !important;
    }
    .rule-desc {
      .font-28-regular;
    }
  }
}
.coupon-dialog-info {
  padding: 0 4 / @rem;
  .sp-info-wrapper {
    display: flex;
    align-items: center;
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    img {
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-radius: 50%;
      width: 64 / @rem;
      height: 64 / @rem;
      display: block;
      margin-right: 16 / @rem;
    }
  }
  .coupon-info-wrapper {
    margin-top: 16 / @rem;
    margin-bottom: 40 / @rem;
  }
  .rule-info-wrapper {
    .rule-row {
      display: flex;
      align-items: flex-start;
      margin-bottom: 16 / @rem;
      .rule-left {
        .font-24-regular(#0000004d);
        margin-right: 24 / @rem;
        min-width: 96 / @rem;
        .en & {
          word-break: break-word;
          font-size: 26 / @rem;
          width: 136 / @rem;
        }
      }
      .rule-right {
        .font-24-regular(#1A1A1A);
        flex: 1;
        .en & {
          font-size: 26 / @rem;
          line-height: 34 / @rem;
        }
      }
    }
  }
  .coupon-tandc {
    display: flex;
    align-items: center;
    .font-24-regular(#0000004d);
    .arrow-right {
      width: 16 / @rem;
      height: 32 / @rem;
      margin-left: 16 / @rem;
    }
    .en & {
      font-size: 26 / @rem;
    }
  }
  .use-scene {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 32 / @rem 0;
    padding-top: 32 / @rem;
    display: flex;
    align-items: flex-start;
    .left {
      display: inline-block;
      width: 120 / @rem;
      min-width: 120 / @rem;
      .font-24-regular(#0000004d);
      .en & {
        word-break: break-word;
        width: 200 / @rem;
        font-size: 26 / @rem;
        line-height: 34 / @rem;
      }
    }
    .right {
      display: flex;
      align-items: flex-start;
      width: 100%;
      justify-content: space-between;
      .right-item {
        display: flex;
        align-items: center;
        flex-direction: column;
        .font-24-regular(#00000073);
        text-align: center;
        min-width: 80 / @rem;
        max-width: 180 / @rem;
      }
      .en & {
        .right-item.second {
          min-width: 140 / @rem;
        }
        .right-item.first {
          min-width: 180 / @rem;
        }
      }
      .svg-icon {
        width: 48 / @rem;
        height: 48 / @rem;
      }
    }
  }
}

.expire-tip {
  .font-24-regular(#ffffff);
  background-color: #FA5151;
  height: 34 / @rem;
  line-height: 34/ @rem;
  padding: 2/@rem 12/ @rem;
  border-radius: 16/ @rem;
  margin-left: 8/@rem;

  .en & {
    font-size: 22 / @rem;
  }
}

.dialog-footer {
  margin: 64 / @rem auto;
  .coupon-need {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24 / @rem;
    text-align: center;
    color: rgba(0, 0, 0, 0.9);
    margin: 0 auto 8 / @rem auto;

    .stamp {
      margin-left: 9 / @rem;
      margin-bottom: 8 / @rem;
      display: flex;
      align-items: flex-end;
      /deep/ .stamp-item {
        display: inline-flex;
        img {
          width: 30 / @rem;
          height: 30 / @rem;
          vertical-align: bottom;
        }
      }
      span {
        margin-left: 10 / @rem;
        color: #1ec76f;
        display: flex;
        align-items: center;
        vertical-align: bottom;
        .font-24-medium;
        line-height: 22 / @rem;
      }
    }
  }
  // 限时抢券提示
  .lock-tip {
    text-align: center;
    margin-top: 8/@rem;
  }
}
</style>
